import { ComponentRef, inject, Injectable, Injector } from '@angular/core';
import { outputToObservable } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Asset, AssetFacade } from '@simOn/asset';
import { AssetsApiService } from '@simOn/asset/element/data-access';
import { AssetHyperlinks, TAsset, TAssetEvent, TBaseAssetInfo } from '@simOn/asset/modifying/models';
import { Currency, CurrencyEnum } from '@simOn/common/models';
import { SidenavRightPortalService } from '@simOn/layout';
import { SpaceFacade } from '@simOn/space';
import { delay, filter, firstValueFrom, merge, Subject, take, takeUntil } from 'rxjs';
import { AgentActionOutcome, AgentAssetPayload, CreateAssetActionDto, UpdateAssetActionDto } from '../model';
import { toAssetFormEvents, toAssetFormHyperlinks } from './agent-asset-draft.mappers';
import { AgentAssetNavigationService } from './agent-asset-navigation.service';
import { AgentPanelHostService } from './agent-panel-host.service';

const PANEL_WIDTH = 'min(520px,100%)';

// eslint-disable-next-line @nx/enforce-module-boundaries
type FeatureAssetChunk = typeof import('feature-asset');
type ModifyingAssetRef = ComponentRef<InstanceType<FeatureAssetChunk['ModifyingAssetComponent']>>;

/** Draft values for the three sections of the asset form; `undefined` clears a field. */
interface AssetFormValues {
  baseInfo: TBaseAssetInfo;
  technical: { brand?: string; model?: string; serialNumber?: string };
  purchase: {
    purchasePrice?: number;
    purchaseCurrency?: Currency;
    currentValue?: number;
    currentCurrency?: Currency;
    purchaseDate?: string;
    warrantyEnd?: string;
  };
  /** Complete list of reminders to replace the form's; `undefined` leaves the form's list alone. */
  events?: TAssetEvent[];
  /** Complete list of links; same semantics as `events`. */
  hyperlinks?: AssetHyperlinks[];
}

const toCurrency = (value: string | null | undefined): Currency | undefined =>
  value && value in CurrencyEnum ? (value as Currency) : undefined;

const orUndefined = <T>(value: T | null | undefined): T | undefined => value ?? undefined;

/**
 * Handles the `CreateAsset` and `UpdateAsset` draft cards. Nothing is saved by the agent: the existing
 * asset form opens in the right sidenav, prefilled from the card, and the user reviews and saves it as
 * usual (the form runs the regular `CreateAsset` / `UpdateAsset` requests with all their checks).
 *
 * For an update the CURRENT asset is loaded first and the card's changes are applied on top, so the
 * form (and the PUT body it builds) keeps attachments, reminders, links and the 3D position.
 */
@Injectable({ providedIn: 'root' })
export class AgentAssetDraftService {
  private readonly _host = inject(AgentPanelHostService);
  private readonly _router = inject(Router);
  private readonly _injector = inject(Injector);
  private readonly _assetFacade = inject(AssetFacade);
  private readonly _assetsApi = inject(AssetsApiService);
  private readonly _spaceFacade = inject(SpaceFacade);
  private readonly _assetNavigation = inject(AgentAssetNavigationService);
  private _featureAssetChunk?: Promise<FeatureAssetChunk>;
  private readonly _panelClosed$ = new Subject<void>();

  async create(action: CreateAssetActionDto): Promise<AgentActionOutcome> {
    const { portal, chunk } = await this._prepare(action.apartmentId);
    const { payload } = action;

    this._assetFacade.setSelectedId('');
    const { ref, outcome } = this._openForm(portal, chunk, action);
    this._applyValues(ref, {
      baseInfo: {
        name: payload.name,
        roomId: payload.roomId || action.roomId || ref.instance.assetState.asset().roomId || '',
        categoryId: payload.categoryId ?? null,
        category: null,
        textNote: orUndefined(payload.textNote)
      },
      technical: this._technical(payload, []),
      purchase: this._purchase(payload, []),
      events: payload.events?.length ? toAssetFormEvents(payload.events) : undefined,
      hyperlinks: payload.hyperlinks?.length ? toAssetFormHyperlinks(payload.hyperlinks) : undefined
    });

    // Once the asset is saved, show it the way a "navigate to asset" card would.
    this._assetFacade.addAssetStatus$
      .pipe(
        filter(
          (status): status is ReturnType<typeof Asset.AddAssetSuccess> => status.type === Asset.AddAssetSuccess.type
        ),
        take(1),
        takeUntil(this._panelClosed$)
      )
      .subscribe(({ state }) =>
        this._assetNavigation.navigate({
          targetId: state.id,
          apartmentId: action.apartmentId,
          apartmentName: action.apartmentName
        })
      );
    return outcome;
  }

  async update(action: UpdateAssetActionDto): Promise<AgentActionOutcome> {
    const { portal, chunk } = await this._prepare(action.apartmentId);
    const { payload, clearedFields } = action;
    const current = await firstValueFrom(this._assetsApi.getAsset(action.targetId));
    const cleared = (field: keyof AgentAssetPayload): boolean => clearedFields.includes(field);

    this._assetFacade.setSelectedId(action.targetId);
    // Hydrate the form with the current asset (attachments marked as already stored, like the regular
    // edit flow does) so the card's changes land on top of it and dirty tracking starts from the original.
    this._injector.get(chunk.AssetPlacementHandoffService).arm({
      asset: {
        ...current,
        attachedImages: (current.attachedImages || []).map((image) => ({ ...image, attachMode: 'FromDocument' })),
        attachedDocuments: (current.attachedDocuments || []).map((document) => ({
          ...document,
          attachMode: 'FromDocument'
        }))
      },
      isDirty: false
    });
    const { ref, outcome } = this._openForm(portal, chunk, action);
    const categoryId = cleared('categoryId') ? null : (payload.categoryId ?? current.categoryId);
    this._applyValues(ref, {
      baseInfo: {
        name: payload.name ?? current.name,
        roomId: payload.roomId ?? current.roomId,
        categoryId,
        category: categoryId === current.categoryId ? current.category : null,
        textNote: cleared('textNote') ? undefined : (payload.textNote ?? current.textNote)
      },
      technical: this._technical(payload, clearedFields, current),
      purchase: this._purchase(payload, clearedFields, current),
      // A list on the card is the COMPLETE new list (an empty array removes everything); `null` = unchanged.
      events: Array.isArray(payload.events) ? toAssetFormEvents(payload.events) : undefined,
      hyperlinks: Array.isArray(payload.hyperlinks) ? toAssetFormHyperlinks(payload.hyperlinks) : undefined
    });
    return outcome;
  }

  private async _prepare(
    apartmentId: string
  ): Promise<{ portal: SidenavRightPortalService; chunk: FeatureAssetChunk }> {
    const portal = await this._host.portalFor(apartmentId, ['/space', apartmentId, 'dashboard', 'assets']);
    if (!portal) throw new Error('[ai-agent] no panel host for the asset form');
    // Asset requests are scoped by the selected space (header interceptor); make sure it is the card's one.
    this._spaceFacade.setSelectedId(apartmentId);
    // eslint-disable-next-line @nx/enforce-module-boundaries
    const chunk = await (this._featureAssetChunk ??= import('feature-asset'));
    return { portal, chunk };
  }

  /**
   * Opens the asset form in the portal. `outcome` resolves `saved` once the facade reports a successful add /
   * update, or `dismissed` once the panel closes without one (cancel, "set position", another card replacing it).
   */
  private _openForm(
    portal: SidenavRightPortalService,
    chunk: FeatureAssetChunk,
    action: CreateAssetActionDto | UpdateAssetActionDto
  ): { ref: ModifyingAssetRef; outcome: Promise<AgentActionOutcome> } {
    this._panelClosed$.next();
    const outcome = new Promise<AgentActionOutcome>((resolve) => {
      // The form closes itself right after a successful save, so a close counts as a dismissal only a tick later.
      merge(this._assetFacade.addAssetStatus$, this._assetFacade.updateAssetStatus$)
        .pipe(
          filter(({ type }) => type === Asset.AddAssetSuccess.type || type === Asset.UpdateAssetSuccess.type),
          take(1),
          takeUntil(this._panelClosed$.pipe(delay(0)))
        )
        .subscribe(() => resolve('saved'));
      this._panelClosed$.pipe(take(1)).subscribe(() => setTimeout(() => resolve('dismissed')));
    });
    const ref = portal.open(chunk.ModifyingAssetComponent, {
      width: PANEL_WIDTH,
      hasBackdrop: true,
      disableClose: true
    });
    if (action.apartmentName) ref.setInput('spaceName', action.apartmentName);

    const close = (): void => {
      this._panelClosed$.next();
      portal.close();
    };
    portal
      .backdropClicked$()
      .pipe(takeUntil(this._panelClosed$))
      .subscribe(() => ref.instance.close());
    merge(
      outputToObservable(ref.instance.closePanel),
      outputToObservable(ref.instance.modificationFinished),
      outputToObservable(ref.instance.resetAll)
    )
      .pipe(take(1), takeUntil(this._panelClosed$))
      .subscribe(close);
    // "Set position" hands the draft over to the 3D walk, the same way the assets page does.
    outputToObservable(ref.instance.editPosition)
      .pipe(take(1), takeUntil(this._panelClosed$))
      .subscribe(() => {
        const { assetState } = ref.instance;
        this._injector.get(chunk.AssetPlacementHandoffService).arm({
          asset: assetState.asset(),
          isDirty: assetState.isDirty(),
          initialSnapshot: assetState.initialSnapshot()
        });
        close();
        this._router.navigate(['/space', action.apartmentId, 'apartment'], {
          queryParams: { context: 'assets', editAssetPosition: true }
        });
      });
    return { ref, outcome };
  }

  /** Patches the form state right after attach, before the first render, so every section picks the values up. */
  private _applyValues(ref: ModifyingAssetRef, values: AssetFormValues): void {
    const state = ref.instance.assetState;
    state.setBaseInfo(values.baseInfo);
    state.setTechnicalInfo(values.technical);
    state.setPurchaseWarranty(values.purchase);
    if (values.events) state.setEvents(values.events);
    if (values.hyperlinks) state.setHyperlinks(values.hyperlinks);
  }

  private _technical(
    payload: Partial<AgentAssetPayload>,
    clearedFields: string[],
    current?: TAsset
  ): AssetFormValues['technical'] {
    const pick = (field: 'brand' | 'model' | 'serialNumber'): string | undefined =>
      clearedFields.includes(field) ? undefined : orUndefined(payload[field] ?? current?.[field]);
    return { brand: pick('brand'), model: pick('model'), serialNumber: pick('serialNumber') };
  }

  private _purchase(
    payload: Partial<AgentAssetPayload>,
    clearedFields: string[],
    current?: TAsset
  ): AssetFormValues['purchase'] {
    const pick = <K extends 'purchasePrice' | 'currentValue' | 'purchaseDate' | 'warrantyEnd'>(
      field: K
    ): NonNullable<AgentAssetPayload[K]> | undefined =>
      clearedFields.includes(field)
        ? undefined
        : (orUndefined(payload[field] ?? current?.[field]) as NonNullable<AgentAssetPayload[K]> | undefined);
    const currency = (field: 'purchaseCurrency' | 'currentCurrency'): Currency | undefined =>
      clearedFields.includes(field) ? undefined : (toCurrency(payload[field]) ?? current?.[field]);
    return {
      purchasePrice: pick('purchasePrice'),
      purchaseCurrency: currency('purchaseCurrency'),
      currentValue: pick('currentValue'),
      currentCurrency: currency('currentCurrency'),
      purchaseDate: pick('purchaseDate'),
      warrantyEnd: pick('warrantyEnd')
    };
  }
}
